// newsListHelper.tsx
import React, { JSX, ReactNode,useCallback } from 'react';
import { NewsArticle } from './NewsAPIUtil';
import ImageCarousel from 'src/components/ImageCarousel';
import type { Components } from 'react-markdown';

import { useParams, useLocation, useNavigate } from "react-router-dom";

// Get full path from location
export const useCurrentPath = (): string => {
    const location = useLocation();
    return location.pathname; // e.g. /cn/news/123
  };


// Extract language from path
export const getLanguageFromPath = () => {
    const path = window.location.pathname;
    if (path.startsWith('/cn')) return 'zh-Hans';
    if (path.startsWith('/tw')) return 'zh-Hant';
    if (path.startsWith('/es')) return 'es';
    if (path.startsWith('/fr')) return 'fr';
    return 'en';
};


// Extract language from path
export const getPathPrefix = (): string => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path.startsWith('/cn')) return 'cn';
    if (path.startsWith('/tw')) return 'tw';
    if (path.startsWith('/es')) return 'es';
    if (path.startsWith('/fr')) return 'fr';
    return 'en';
};




export const useNewsHreflangTags = (availLangs: string[]) => {
    const location = useLocation();
    const currentPath = location.pathname;
    const baseUrl = window.location.origin;
    const getLocalizedUrl = (lang: string) => {
        const localizedPath = currentPath.replace(/^\/(en|cn|tw|es|fr)/, `/${lang}`);
        return `${baseUrl}${localizedPath}`;
    };

    const updateHreflangTags = () => {
        const head = document.head;
        const existingTags = document.querySelectorAll('link[rel="alternate"][hreflang]');
        existingTags.forEach((tag) => head.removeChild(tag));

        availLangs.forEach((lang) => {
            const link = document.createElement('link');
            link.rel = 'alternate';
            link.hreflang = lang;
            link.href = getLocalizedUrl(lang);
            head.appendChild(link);
        });

        // Optionally add x-default if 'en' is available
        if (availLangs.includes('en')) {
            const link = document.createElement('link');
            link.rel = 'alternate';
            link.hreflang = 'x-default';
            link.href = getLocalizedUrl('en');
            head.appendChild(link);
        }
    };

    return { updateHreflangTags };
};
// Helper function to process footnote references
export const addStructuredData = (article: NewsArticle | null) => {
    if (!article) return;

    const schemaData: any = {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "headline": article.title || "Dummy Headline",
        "image": [
            article.img_urls[0] || "https://global24.news/static/favicon.ico"
        ],
        "datePublished": article.postTime
            ? new Date(article.postTime * 1000).toISOString()
            : new Date().toISOString(),
        "dateModified": article.postTime
            ? new Date(article.postTime * 1000).toISOString()
            : new Date().toISOString(),
        "author": {
            "@type": "Person",
            "name": "Global24",
            "url": "https://x.com/Global24_News"
        },
        "publisher": {
            "@type": "Organization",
            "name": "Global24",
            "logo": {
                "@type": "ImageObject",
                "url": "https://global24.news/static/favicon.ico",
                "width": 600,
                "height": 60,
            }
        },
        "description": article.news
            ? article.news.substring(0, 200) + "..."
            : "Global24 News is a news website that provides news from around the world.",
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": window.location.href,
        },
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schemaData);
    document.head.appendChild(script);
};

export const renderMedia = (news: NewsArticle) => {
    if (!news) return null;

    // Prepare media array with both videos and images
    const mediaItems = [];

    // Add video first if it exists
    if (news.video_embed_link) {
        mediaItems.push({
            type: 'video' as const,
            url: news.video_embed_link
        });
    }

    // Add images if they exist
    if (news.img_urls && news.img_urls.length > 0) {
        news.img_urls.forEach(url => {
            mediaItems.push({
                type: 'image' as const,
                url: url
            });
        });
    }

    // If we have any media items, render the carousel
    if (mediaItems.length > 0) {
        return (
            <div className="w-full">
                <ImageCarousel
                    media={mediaItems}
                    title={news.title}
                />
            </div>
        );
    }

    return null;
};

// Helper function to process footnote references
export const processFootnotes = (text: string): ReactNode[] => {
    // Support the following formats: [1], [^1], [1][^1], [^1][2], etc.
    // This regex will match any pattern with square brackets containing optional ^ and digits
    const segments = text.split(/(\[\^?\d+\](?:\[\^?\d+\])*)/g);
    return segments.map((segment, index) => {
        if (!segment) return null;

        // Process segments that match any footnote pattern
        if (segment.match(/^\[\^?\d+\]/)) {
            // Extract all reference numbers from the segment
            const numbers = Array.from(segment.matchAll(/\[\^?(\d+)\]/g)).map(match => match[1]);

            if (numbers.length === 0) return segment;

            // Convert each number to a superscript
            return (
                <React.Fragment key={index}>
                    {numbers.map((num, i) => (
                        <sup key={i} className="text-xs font-medium text-gray-600 ml-0.5">{num}</sup>
                    ))}
                </React.Fragment>
            );
        }

        return segment;
    }).filter(Boolean);
};

// Custom components for ReactMarkdown
export const components: Partial<Components> = {
    // Add h2 support for ## markdown headers
    h2: ({ children, ...props }) => (
        <h2 className="text-3xl font-bold text-gray-900 mb-5 mt-10 border-b pb-2 border-gray-200" {...props}>{children}</h2>
    ),
    // Override h3 rendering
    h3: ({ children, ...props }) => (
        <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" {...props}>{children}</h3>
    ),

    // Override paragraph rendering
    p: ({ children, ...props }) => {
        if (typeof children === 'string') {
            // Process superscript references in the paragraph text
            const processedChildren = processFootnotes(children);
            return <p className="mb-6 text-base" {...props}>{processedChildren}</p>;
        }
        return <p className="mb-6 text-base" {...props}>{children}</p>;
    },
    // Override strong/bold rendering
    strong: ({ children, ...props }) => (
        <strong className="font-bold" {...props}>{children}</strong>
    ),
    // Add custom handling for links
    a: ({ href, children, ...props }) => (
        <a href={href} className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer" {...props}>
            {children}
        </a>
    ),
    // Add custom handling for lists
    ul: ({ children, ...props }) => (
        <ul className="list-disc pl-6 mb-6" {...props}>{children}</ul>
    ),
    ol: ({ children, ...props }) => (
        <ol className="list-decimal pl-6 mb-6" {...props}>{children}</ol>
    ),
    li: ({ children, ...props }) => {
        if (typeof children === 'string') {
            // Process superscript references in list items
            const processedChildren = processFootnotes(children);
            return <li className="mb-2" {...props}>{processedChildren}</li>;
        }
        return <li className="mb-2" {...props}>{children}</li>;
    },
    // Add custom handling for blockquotes
    blockquote: ({ children, ...props }) => (
        <blockquote className="border-l-4 border-gray-300 pl-4 italic my-6" {...props}>{children}</blockquote>
    )
};
